import { TemplateEngine } from "../shared/utils/TemplateEngine.js";
import { AppConfig } from "../shared/utils/AppConfig.js";
import { ApiService } from "../shared/utils/ApiService.js";
import { Format } from "../shared/utils/Format.js";
import { MuestreosService } from "../shared/services/MuestreosService.js";
import { Navbar } from "../shared/components/navbar/Navbar.js";
import muestreosTemplate from "./templates/muestreos.html?raw";
import equiposTemplate from "./templates/equipos.html?raw";
import Swal from "sweetalert2";
import { confirmarBorrado } from "../shared/utils/Confirmar.js";
import { Sesion } from "../shared/utils/Sesion.js";
import { getNombre } from "../shared/services/LcapiiService.js";
import { Estadisticas } from "./Estadisticas.js";

const esc = (v) =>
  String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const estadoBadge = (estado) => {
  const map = {
    Procesado: "text-bg-success",
    Pendiente: "text-bg-secondary",
    Error: "text-bg-danger",
  };
  const cls = map[estado] || "text-bg-light";
  return '<span class="badge ' + cls + '">' + esc(estado) + "</span>";
};

// Columna Familias: cantidad + las dos primeras; si hay más, "…" y una lupa que abre el detalle.
const familiasHtml = (m) => {
  const fams = m.familias || [];
  if (fams.length === 0) return '<span class="metrica">' + esc(Format.num(m.cantFamilias)) + "</span>";
  const primeras = fams.slice(0, 2).map((f) => '<span title="' + esc(f.nombre) + '">' + esc(f.codigo) + "</span>").join(", ");
  return (
    '<span class="metrica fw-semibold me-1">' + fams.length + "</span>" +
    '<span class="text-muted small">(' + primeras + (fams.length > 2 ? "…" : "") + ")</span>" +
    (fams.length > 2
      ? '<button type="button" class="btn btn-link btn-sm p-0 ms-1 btnFamilias" data-id="' + m.id + '" title="Ver todas las familias">' +
        '<i class="fas fa-magnifying-glass"></i></button>'
      : "")
  );
};

// Filtros vigentes, leídos del hash (#muestreos?sucursal=..&desde=..&hasta=..&familia=..).
const leerFiltros = (params) => ({
  // Sin sucursal, el visor arranca en "Sin asignar": la bandeja de equipos que todavía
  // hay que vincular a una sucursal.
  sucursal: params?.get("sucursal") || "sin-asignar",
  desde: params?.get("desde") || "",
  hasta: params?.get("hasta") || "",
  familia: params?.get("familia") || "",
});

const paramsActuales = () => new URLSearchParams(window.location.hash.split("?")[1] || "");

const periodoTxt = (f) => {
  const d = (iso) => Format.fecha(iso + "T00:00:00");
  if (f.desde && f.hasta) return "del " + d(f.desde) + " al " + d(f.hasta);
  if (f.desde) return "desde el " + d(f.desde);
  if (f.hasta) return "hasta el " + d(f.hasta);
  return "(todo el historial)";
};

export const MuestreosController = {
  render: async (params) => {
    const app = document.getElementById("app");
    const filtros = leerFiltros(params);
    const sucursalFiltro = filtros.sucursal;

    app.innerHTML = Navbar.render("muestreos") + MuestreosController.loaderHtml();
    Navbar.bind();

    try {
      const apiUrl = AppConfig.resolvedApiUrl();
      const [{ muestreos, resumenSucursales, familiasDisponibles }, sucursales] = await Promise.all([
        MuestreosService.listar(filtros),
        MuestreosService.sucursales(),
      ]);

      const esSinAsignar = sucursalFiltro === "sin-asignar";
      const filtroNum = esSinAsignar || !sucursalFiltro ? null : Number(sucursalFiltro);
      // Los equipos sin sucursal no tienen cadencia que mostrar.
      const resumenVisible = esSinAsignar
        ? []
        : filtroNum
        ? resumenSucursales.filter((r) => r.sucursalId === filtroNum)
        : resumenSucursales;

      MuestreosController._sucursales = sucursales;
      MuestreosController._muestreos = muestreos;
      MuestreosController._filtros = filtros;
      MuestreosController._sucursalTxt = esSinAsignar
        ? "Sin asignar"
        : sucursales.find((s) => s.id === filtroNum)?.nombre || "Sucursal " + sucursalFiltro;

      const res = Estadisticas.resumen(muestreos, filtros.familia || null);

      const data = {
        desde: filtros.desde,
        hasta: filtros.hasta,
        hayFiltroExtra: !!(filtros.desde || filtros.hasta || filtros.familia),
        familiasOpciones: familiasDisponibles.map((f) => ({
          codigo: f.codigo,
          texto: f.codigo + (f.nombre ? " - " + f.nombre : ""),
          selected: f.codigo === filtros.familia,
        })),
        periodoTxt: periodoTxt(filtros),
        difArticulosTxt: Format.num(res.articulosConDiferencia),
        difSobranteTxt: Format.num(res.articulosSobrante),
        difFaltanteTxt: Format.num(res.articulosFaltante),
        difUnidadesTxt: Format.num(res.unidadesDiferencia),
        familiasMuestreadasTxt: Format.num(res.familias),
        cantMuestreosTxt:
          res.muestreos + (res.muestreos === 1 ? " muestreo" : " muestreos") +
          (res.procesados < res.muestreos ? " (" + res.procesados + " procesados)" : ""),
        sinAsignarSelected: esSinAsignar,
        puedeGestionarEquipos: Sesion.puedeGestionarEquipos(),
        sucursales: sucursales.map((s) => ({ id: s.id, nombre: s.nombre, selected: s.id === filtroNum })),
        muestreos: muestreos.map((m) => ({
          id: m.id,
          inventario: m.inventario,
          sucursalTxt: m.sucursalNombre || "Sin asignar",
          asignado: !!m.sucursalId,
          deviceId: m.deviceId,
          sucursalNombre: m.sucursalNombre || "",
          puedeBorrar: Sesion.puedeEliminarMuestreo(),
          cierreTxt: Format.fechaHora(m.fechaCierre),
          estadoBadge: estadoBadge(m.estado),
          itemsTxt: Format.num(m.totalItemsLeidos),
          articulosTxt: Format.num(m.totalArticulos),
          diferenciasTxt: Format.num(m.totalDiferencias),
          familiasHtml: familiasHtml(m),
          archivoUrl: apiUrl + "/muestreos/" + m.id + "/archivo",
          tieneDetalle: !!m.tieneDetalle,
          sectoresUrl: apiUrl + "/muestreos/" + m.id + "/sectores",
        })),
        hayMuestreos: muestreos.length > 0,
        resumenSucursales: resumenVisible.map((r) => ({
          sucursalNombre: r.sucursalNombre,
          cantMuestreos: r.cantMuestreos,
          promedioTxt: r.promedioDias === null || r.promedioDias === undefined ? "—" : Format.num(r.promedioDias),
          diasDesdeUltimo: r.diasDesdeUltimo === null || r.diasDesdeUltimo === undefined ? "—" : r.diasDesdeUltimo,
        })),
      };

      app.innerHTML = Navbar.render("muestreos") + TemplateEngine.render(muestreosTemplate, data);
      Navbar.bind();
      MuestreosController.bind();
    } catch (error) {
      console.error("Error cargando muestreos:", error);
      app.innerHTML = Navbar.render("muestreos") + MuestreosController.errorHtml(error);
      Navbar.bind();
      document.getElementById("btnReintentar")?.addEventListener("click", () => MuestreosController.render(params));
    }
  },

  bind: () => {
    // Cambiar de sucursal conserva las fechas pero no la familia (la otra sucursal puede no tenerla).
    document.getElementById("selSucursal")?.addEventListener("change", (e) => {
      MuestreosController.irA({ ...MuestreosController._filtros, sucursal: e.target.value, familia: "" });
    });

    document.querySelectorAll(".filtro-muestreos").forEach((el) => {
      el.addEventListener("change", () => {
        const desde = document.getElementById("inpDesde").value;
        const hasta = document.getElementById("inpHasta").value;
        if (desde && hasta && desde > hasta) {
          Swal.fire({ icon: "warning", title: "Rango inválido", text: "La fecha desde no puede ser posterior a la fecha hasta." });
          return;
        }
        MuestreosController.irA({
          ...MuestreosController._filtros,
          desde,
          hasta,
          familia: document.getElementById("selFamilia").value,
        });
      });
    });

    document.getElementById("btnLimpiarFiltros")?.addEventListener("click", () => {
      MuestreosController.irA({ sucursal: MuestreosController._filtros?.sucursal });
    });

    document.getElementById("btnRefrescar")?.addEventListener("click", () => {
      ApiService.invalidate("muestreos");
      MuestreosController.render(paramsActuales());
    });

    document.getElementById("btnReporte")?.addEventListener("click", MuestreosController.generarReporte);

    document.querySelectorAll(".btnFamilias").forEach((btn) => {
      btn.addEventListener("click", () => MuestreosController.verFamilias(Number(btn.dataset.id)));
    });

    document.getElementById("btnEquipos")?.addEventListener("click", MuestreosController.abrirEquipos);

    document.querySelectorAll(".btnOrigen").forEach((btn) => {
      btn.addEventListener("click", () => MuestreosController.verOrigen(btn.dataset.device));
    });

    document.querySelectorAll(".btnBorrar").forEach((btn) => {
      btn.addEventListener("click", () => MuestreosController.borrar(btn.dataset.id, btn.dataset.nombre, btn.dataset.sucursal));
    });
  },

  irA: ({ sucursal, desde, hasta, familia }) => {
    const q = new URLSearchParams();
    if (sucursal) q.set("sucursal", sucursal);
    if (desde) q.set("desde", desde);
    if (hasta) q.set("hasta", hasta);
    if (familia) q.set("familia", familia);
    const qs = q.toString();
    window.location.hash = "muestreos" + (qs ? "?" + qs : "");
  },

  verFamilias: (id) => {
    const m = (MuestreosController._muestreos || []).find((x) => x.id === id);
    if (!m) return;
    const filas = (m.familias || [])
      .map(
        (f) =>
          '<tr><td class="fw-semibold">' + esc(f.codigo) + "</td><td>" + esc(f.nombre || "—") + "</td>" +
          '<td class="text-end metrica">' + esc(Format.num(f.articulos)) + "</td>" +
          (f.articulosSobrante
            ? '<td class="text-end metrica text-success">' + esc(Format.num(f.articulosSobrante)) + "</td>"
            : '<td class="text-end text-muted">—</td>') +
          (f.articulosFaltante
            ? '<td class="text-end metrica text-danger">' + esc(Format.num(f.articulosFaltante)) + "</td>"
            : '<td class="text-end text-muted">—</td>') +
          "</tr>"
      )
      .join("");
    Swal.fire({
      title: "Familias del muestreo #" + esc(m.id),
      width: 640,
      html:
        '<p class="small text-muted mb-2">' + esc(m.inventario) + " · " + esc(Format.fechaHora(m.fechaCierre)) + "</p>" +
        '<div class="table-responsive" style="max-height:55vh"><table class="table table-sm align-middle text-start mb-0">' +
        '<thead class="table-light"><tr><th>Código</th><th>Familia</th><th class="text-end">Arts.</th>' +
        '<th class="text-end" title="Artículos con sobrante">Sobr.</th>' +
        '<th class="text-end" title="Artículos con faltante">Falt.</th></tr></thead>' +
        "<tbody>" + filas + "</tbody></table></div>",
      confirmButtonText: "Cerrar",
    });
  },

  generarReporte: async () => {
    const muestreos = MuestreosController._muestreos || [];
    if (muestreos.length === 0) return;
    const btn = document.getElementById("btnReporte");
    const htmlOriginal = btn?.innerHTML;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Generando…';
    }
    try {
      // jsPDF se descarga recién acá, no al cargar la pantalla.
      const { generarReportePdf } = await import("./ReportePdf.js");
      const f = MuestreosController._filtros || {};
      await generarReportePdf({
        muestreos,
        sucursal: MuestreosController._sucursalTxt,
        desde: f.desde,
        hasta: f.hasta,
        familia: f.familia,
        usuario: getNombre(),
      });
    } catch (error) {
      console.error("Error generando el reporte:", error);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo generar el reporte: " + (error.message || "") });
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = htmlOriginal;
      }
    }
  },

  borrar: async (id, nombre, sucursal) => {
    const ok = await confirmarBorrado({
      titulo: "¿Borrar muestreo?",
      html: "Vas a borrar el muestreo <b>" + esc(nombre) + "</b> (#" + esc(id) + ")" +
        (sucursal ? " de <b>" + esc(sucursal) + "</b>" : "") +
        ". Deja de aparecer en el listado y en la cadencia de la sucursal.",
    });
    if (!ok) return;
    try {
      await MuestreosService.borrar(id);
      await Swal.fire({ icon: "success", title: "Eliminado", timer: 1200, showConfirmButton: false });
      MuestreosController.render(paramsActuales());
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo borrar: " + (error.message || "") });
    }
  },

  // Muestreo sin sucursal: muestra el equipo (deviceId) que lo mandó y, con permiso,
  // permite asignarlo ahí mismo sin pasar por el modal de equipos.
  verOrigen: async (deviceId) => {
    const delEquipo = (MuestreosController._muestreos || []).filter((m) => m.deviceId === deviceId);
    const puedeAsignar = Sesion.puedeGestionarEquipos();

    const html =
      '<p class="mb-2">Este muestreo vino del equipo:</p>' +
      '<p><code class="fs-6 user-select-all">' + esc(deviceId) + "</code></p>" +
      (delEquipo.length > 0
        ? '<p class="small text-muted mb-1">Muestreos de este equipo en el listado:</p>' +
          '<ul class="small text-start mb-0">' +
          delEquipo.map((m) => "<li>" + esc(m.inventario) + " (" + esc(Format.fechaHora(m.fechaCierre)) + ")</li>").join("") +
          "</ul>"
        : "") +
      (puedeAsignar ? "" : '<p class="small text-muted mt-3 mb-0">Para asignarlo a una sucursal hace falta el permiso de gestionar equipos.</p>');

    const r = await Swal.fire({
      title: "Equipo de origen",
      icon: "info",
      html,
      showConfirmButton: puedeAsignar,
      confirmButtonText: "Asignar a sucursal",
      showCancelButton: true,
      cancelButtonText: "Cerrar",
    });
    if (!r.isConfirmed) return;

    if (await MuestreosController.asignar(deviceId, "", "")) {
      MuestreosController.render(paramsActuales());
    }
  },

  // --- Gestión de equipos (modal) --------------------------------------------------------
  abrirEquipos: async () => {
    const modalEl = document.getElementById("modalEquipos");
    const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
    MuestreosController._equiposDirty = false;
    // Al cerrar el modal, si hubo cambios, recién ahí re-renderizamos la tabla de fondo
    // (no se puede re-renderizar #app con el modal abierto: se borraría su DOM).
    modalEl.addEventListener(
      "hidden.bs.modal",
      () => {
        if (MuestreosController._equiposDirty) {
          MuestreosController._equiposDirty = false;
          MuestreosController.render(paramsActuales());
        }
      },
      { once: true }
    );
    modal.show();
    await MuestreosController.cargarEquipos();
  },

  cargarEquipos: async () => {
    const body = document.getElementById("modalEquiposBody");
    body.innerHTML = '<div class="loader-centro"><div class="spinner-border text-primary"></div></div>';
    try {
      const [equipos, sucursales] = await Promise.all([MuestreosService.equipos(), MuestreosService.sucursales()]);
      MuestreosController._sucursales = sucursales;

      const data = {
        equipos: equipos.map((e) => ({
          deviceId: e.deviceId,
          alias: e.alias || "",
          aliasRaw: e.alias || "",
          asignado: e.asignado,
          sucursalNombre: e.sucursalNombre || "",
          sucursalIdRaw: e.sucursalId ?? "",
          cantMuestreos: e.cantMuestreos,
          accionTxt: e.asignado ? "Editar" : "Asignar",
        })),
        hayEquipos: equipos.length > 0,
      };

      body.innerHTML = TemplateEngine.render(equiposTemplate, data);
      body.querySelectorAll(".btnAsignar").forEach((btn) => {
        btn.addEventListener("click", async () => {
          if (await MuestreosController.asignar(btn.dataset.device, btn.dataset.alias, btn.dataset.sucursal)) {
            MuestreosController._equiposDirty = true;
            await MuestreosController.cargarEquipos();
          }
        });
      });
    } catch (error) {
      body.innerHTML = '<div class="alert alert-danger mb-0">No se pudieron cargar los equipos: ' + esc(error.message || "") + "</div>";
    }
  },

  asignar: async (deviceId, aliasActual, sucursalActual) => {
    const sucursales = MuestreosController._sucursales || [];
    const opciones = sucursales
      .map((s) => '<option value="' + s.id + '"' + (String(s.id) === String(sucursalActual) ? " selected" : "") + ">" + esc(s.nombre) + "</option>")
      .join("");

    const html =
      '<label class="form-label w-100 text-start">Sucursal</label>' +
      '<select id="swalSucursal" class="form-select mb-3"><option value="">Elegí una sucursal...</option>' + opciones + "</select>" +
      '<label class="form-label w-100 text-start">Alias (opcional)</label>' +
      '<input id="swalAlias" class="form-control" value="' + esc(aliasActual) + '" placeholder="Ej: Colector Depósito" />';

    const r = await Swal.fire({
      title: "Asignar equipo",
      html,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const s = document.getElementById("swalSucursal").value;
        const a = document.getElementById("swalAlias").value;
        if (!s) {
          Swal.showValidationMessage("Elegí una sucursal");
          return false;
        }
        return { sucursalId: parseInt(s, 10), alias: a };
      },
    });
    if (!r.isConfirmed) return false;

    // Devuelve true si guardó; el refresco lo decide quien llama (modal o listado).
    try {
      await MuestreosService.asignarEquipo(deviceId, r.value.sucursalId, r.value.alias);
      return true;
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo asignar: " + (error.message || "") });
      return false;
    }
  },

  loaderHtml: () => '<div class="loader-centro"><div class="spinner-border text-primary" role="status"></div></div>',

  errorHtml: (error) =>
    '<div class="container mt-3"><div class="alert alert-danger d-flex justify-content-between align-items-center">' +
    '<span><i class="fas fa-triangle-exclamation me-2"></i>' + (error.message || "Error al cargar") + "</span>" +
    '<button id="btnReintentar" class="btn btn-sm btn-outline-danger">Reintentar</button></div></div>',
};
